import { Check, ShoppingBag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import groupBy from 'lodash/groupBy';
import { Link } from '@tanstack/react-router';
import dayjs from 'dayjs';
import cartStore from '@/stores/cart.store.ts';

const { VITE_LOCALE } = import.meta.env;

export default function Cart() {
	const { t } = useTranslation();

	const { renderItems, deleteItem, isCartOpen, closeCart } = cartStore(
		useShallow((state) => ({
			renderItems: state.items,
			deleteItem: state.deleteItem,
			isCartOpen: state.isCartOpen,
			closeCart: state.closeCart,
		})),
	);

	const currencyFormatter = new Intl.NumberFormat(VITE_LOCALE, {
		style: 'currency',
		currency: VITE_LOCALE === 'pl' ? 'PLN' : 'PLN',
	});

	const data = groupBy(renderItems, 'service');

	const total = renderItems?.reduce((acc, item) => acc + item?.price, 0);
	const totalFormatted = currencyFormatter.format(total);

	return (
		<div className="cart z-200">
			<div
				className="cart-overlay fixed top-0 left-0 w-full h-full transition-all opacity-0 invisible data-[visible=true]:visible data-[visible=true]:opacity-100 backdrop-blur-[4px]  bg-black/20 z-50"
				onClick={closeCart}
				data-visible={isCartOpen}
			/>
			<div
				className="cart-box z-60 flex flex-col shadow-2xl w-[480px] max-w-[100%] h-[calc(100svh_-_40px)] fixed top-[20px] right-[20px] transition-all max-md:w-[100%] max-md:rounded-none max-md:top-0 max-md:right-0 max-md:h-[100dvh] max-md:min-h-[100svh] max-md:transition-transform bg-white rounded-xl translate-x-[120%] data-[visible=true]:translate-x-0 opacity-0 invisible data-[visible=true]:visible data-[visible=true]:opacity-100"
				data-visible={isCartOpen}
			>
				<div className="flex items-center gap-4 justify-between w-full p-4 border-b border-neutral-200">
					<h2 className="text-4xl font-medium text-[var(--primary)]">{t('cart.title')}</h2>
					<button
						className="cursor-pointer rounded-xl border border-neutral-200 p-2 text-neutral-600 transition-all hover:border-orange-600 active:scale-97"
						onClick={closeCart}
					>
						<X />
					</button>
				</div>

				<div className="flex-1 w-full overflow-y-auto p-4 gap-2 flex flex-col">
					{renderItems?.length ? (
						Object.entries(data)?.map(([service, value], index) => {
							return (
								<div className="w-full flex flex-col gap-2" key={`${service}-${index}`}>
									<span className="font-medium text-lg text-[var(--primary)] truncate">
										{service}
									</span>

									{value?.map((item) => {
										const price = currencyFormatter.format(item?.price);

										return (
											<div
												key={item.id}
												className="w-full flex items-center justify-start gap-4 p-2  bg-neutral-100 rounded-xl"
											>
												<div className="w-full flex flex-col gap-2">
													<div className="flex items-center justify-start gap-4">
														<div className="w-16 h-16 shrink-0 flex items-center justify-center bg-neutral-200 rounded-xl overflow-hidden">
															<img
																className="w-full h-full object-contain data-[cover=true]:object-cover"
																src={item?.serviceImage || '/icons/service.png'}
																alt={item?.name || '•••••••••••••••'}
																data-cover={!!item?.serviceImage}
															/>
														</div>
														<div className="flex flex-col gap-1">
															<Link
																to="/services/$serviceId"
																params={{
																	serviceId: `${item?.serviceId}`,
																}}
																className="text-sm underline font-bold text-neutral-700 truncate max-w-[280px] max-md:max-w-[220px] max-sm:max-w-[160px]"
															>
																{item?.name}
															</Link>
															<span className="text-sm text-neutral-500">
																{t('services.sub_service_start_from', { qty: price })}
															</span>
														</div>

														<button
															className="bg-red-100 p-2 ml-auto rounded-xl transition-all cursor-pointer text-red-800 hover:bg-red-200 active:scale-97"
															onClick={() => {
																deleteItem(item.id);
															}}
														>
															<X size="1.25rem" />
														</button>
													</div>

													{item?.date && (
														<div className="text-[13px] flex items-center justify-between gap-2 py-1.5 px-2 transition-all bg-green-400/10 font-normal text-neutral-600 rounded-lg w-full">
															<span className="capitalize">{`${dayjs(item?.date?.day).locale(VITE_LOCALE).format('MMMM, ddd DD')} • ${item?.date?.slot?.startTime} - ${item?.date?.slot?.endTime}`}</span>

															<Check
																size="1.25rem"
																className="text-green-600 data-[disabled=true]:text-neutral-400"
															/>
														</div>
													)}
												</div>
											</div>
										);
									})}
								</div>
							);
						})
					) : (
						<div className="flex flex-1 items-center justify-center flex-col gap-2">
							<ShoppingBag size="5rem" className="text-neutral-200" />
							<p className="text-neutral-400 text-lg">{t('cart.empty')}</p>
						</div>
					)}
				</div>

				<div className="flex flex-col gap-2 p-4 border-t relative border-neutral-200">
					<p className="text-[var(--primary)] text-xl text-right font-bold mb-2">
						{total ? t('services.sub_service_start_from', { qty: totalFormatted }) : totalFormatted}
					</p>

					<div className="w-full flex items-center justify-between gap-2 max-md:flex-wrap max-md:justify-center">
						<button
							className="bg-neutral-200 goldman flex-auto py-2 px-4 text-lg rounded-xl hover:brightness-95 cursor-pointer transition-all active:scale-97"
							onClick={closeCart}
						>
							{t('cart.continue_shopping')}
						</button>

						<Link
							to="/order-service"
							className="bg-[var(--primary)] text-center flex-auto goldman text-lg text-white p-2 px-4 rounded-xl hover:brightness-95 cursor-pointer transition-all active:scale-97 data-[disabled=true]:bg-neutral-400 data-[disabled=true]:pointer-events-none select-none max-md:w-full"
							disabled={!renderItems?.length}
							data-disabled={!renderItems?.length}
						>
							{t('order.checkout')}
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}
